import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";
import { Route, Routes, useParams } from "react-router";

import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";
import useTypedContext from "hooks/useTypedContext";
import { Module } from "types/generated";

import ModuleVersionSecondaryLayout from "../components/SecondaryLayout";
import { VersionContext } from "../Context";
import ModuleVersionInputs from "../Inputs";
import ModuleVersionOutputs from "../Outputs";
import ModuleVersionReadme from "../Readme";
import { GET_VERSION_EXAMPLE } from "./gql";

const ModuleVersionExample = () => {
  const { exampleId } = useParams<{
    exampleId: string;
  }>();

  const { module, version } = useTypedContext(VersionContext);

  const { data, error, loading } = useQuery<{ module: Module }>(GET_VERSION_EXAMPLE, {
    variables: { versionId: version.id, moduleId: module.id },
  });

  const examples = useMemo(
    () => data?.module?.version?.metadata?.examples || [],
    [data?.module?.version?.metadata?.examples]
  );

  const example = useMemo(() => examples.find((e) => e.name === exampleId), [exampleId, examples]);
  const inputs = useMemo(() => example?.inputs || [], [example?.inputs]);
  const outputs = useMemo(() => example?.outputs || [], [example?.outputs]);

  const sidebar = useMemo(
    () => [
      {
        to: "..",
        label: "Readme",
        end: true,
      },
      {
        to: "../inputs",
        label: "Inputs",
        count: inputs.length,
      },
      {
        to: "../outputs",
        label: "Outputs",
        count: outputs.length,
      },
    ],
    [inputs.length, outputs.length]
  );

  const dropdownOptions = useMemo(
    () =>
      examples.map((e) => ({
        to: `/module/${module.id}/version/${version.id}/example/${encodeURIComponent(e.name)}`,
        isActive: e.name === example?.name,
        label: e.name,
      })),
    [examples, module.id, version.id, example?.name]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.module) {
    return <PageLoading />;
  }

  if (!data?.module || !example) {
    return <NotFoundPage />;
  }

  return (
    <ModuleVersionSecondaryLayout
      entityName={example.name}
      title="Example overview"
      sidebar={sidebar}
      dropdownLabel={`Example: ${example.name}`}
      dropdownOptions={dropdownOptions}
      entityTypeLabel="Examples"
      entityTypeLink={`/module/${module.id}/version/${version.id}/examples`}
    >
      <Routes>
        <Route index element={<ModuleVersionReadme content={example.readme} />} />
        <Route path="inputs" element={<ModuleVersionInputs inputs={inputs} />} />
        <Route path="outputs" element={<ModuleVersionOutputs outputs={outputs} />} />
      </Routes>
    </ModuleVersionSecondaryLayout>
  );
};

export default ModuleVersionExample;
